/* eslint-disable no-unused-vars */
import type {
  Product,
  VariableProduct,
  VariationProduct,
} from '@frontend/libs/types/product'

import { create } from 'zustand'

import type { SelectedOption } from '../../features/products/components/swatch/Swatch'

type ProductState = {
  isQuickViewOpen: boolean
  loading: boolean
  product: Product
  selectedOption: SelectedOption
  variation: VariationProduct
}

type ProductActions = {
  clearSelectedOption: () => void
  closeQuickView: () => void
  openQuickView: (product: Product, selectedOption: SelectedOption) => void
  reset: () => void
  setDefaultVariation: (product: VariableProduct) => void
  setIsQuickViewOpen: (isQuickViewOpen: boolean) => void
  setLoading: (loading: boolean) => void
  setProduct: (product: Product) => void
  setSelectedOption: (selectedOption: SelectedOption) => void
  setVariation: (variation: VariationProduct) => void
}

function findVariationProduct(
  variations: VariationProduct[],
  options: SelectedOption,
) {
  return variations?.find((variation) => {
    const terms = variation.terms
    const termsKeys = Object.keys(terms)
    const optionsKeys = Object.keys(options)

    return (
      termsKeys.length === optionsKeys.length &&
      optionsKeys.every((key) => terms[key] === options[key])
    )
  })
}

const initialState: ProductState = {
  isQuickViewOpen: false,
  loading: false,
  product: null,
  selectedOption: null,
  variation: null,
}

export const useProductStore = create<ProductActions & ProductState>(
  (set, get) => {
    const setDefaultVariation = (product: VariableProduct) => {
      const defaultVariation = product?.variations?.[0] || null
      set({
        selectedOption: defaultVariation?.terms,
        variation: defaultVariation,
      })
    }

    const openQuickView = (
      product: Product,
      selectedOption: SelectedOption,
    ) => {
      get().reset()
      get().setLoading(true)
      set({ loading: true, product, selectedOption })
      get().setSelectedOption(selectedOption)

      if (!Object.keys(selectedOption).length) {
        get().setDefaultVariation(product as VariableProduct)
      }

      get().setIsQuickViewOpen(true)
      get().setLoading(false)
    }

    const closeQuickView = () => {
      get().setIsQuickViewOpen(false)
    }

    return {
      ...initialState,
      clearSelectedOption: () => set({ selectedOption: null, variation: null }),
      closeQuickView,
      openQuickView,
      reset: () => set(initialState),
      setDefaultVariation,
      setIsQuickViewOpen: (isQuickViewOpen) => set({ isQuickViewOpen }),
      setLoading: (loading) => set({ loading }),
      setProduct: (product) => set({ product }),
      setSelectedOption: (selectedOption) => {
        if (!selectedOption) return
        const options = { ...get().selectedOption, ...selectedOption }
        const newVariation = findVariationProduct(
          get().product?.variations,
          options,
        )
        set({ selectedOption: options, variation: newVariation })
      },
      setVariation: (variation) => set({ variation }),
    }
  },
)
